// MilestonesPage.jsx
import React, { useEffect, useState } from "react";
import {
  Plus,
  Pencil,
  Trash2,
  CheckCircle,
  XCircle,
} from "lucide-react";
import API from "../../../../api";
import "./MilestonesPage.css";

export default function MilestonesPage() {
  const [projects, setProjects] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [selectedProject, setSelectedProject] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState(null);
  const [form, setForm] = useState({
    title: "",
    due_date: "",
    amount: "",
    status: "pending",
  });
  const [message, setMessage] = useState("");
  const [loadingVerify, setLoadingVerify] = useState({});
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  useEffect(() => {
    loadProjects();
  }, []);

  useEffect(() => {
    if (selectedProject) loadMilestones(selectedProject);
    else setMilestones([]);
  }, [selectedProject]);

  async function loadProjects() {
    try {
      const res = await API.get("/projects");
      setProjects(res.data || []);
    } catch (err) {
      console.error("Projects error", err.response?.data || err.message);
      alert("Failed to load projects");
    }
  }

  async function loadMilestones(projectId) {
    try {
      const res = await API.get(`/milestones/project/${projectId}`);
      setMilestones(res.data || []);
    } catch (err) {
      console.error("Milestones error", err.response?.data || err.message);
      if (err.response?.status === 404) setMilestones([]);
      else alert("Failed to load milestones");
    }
  }

  function handleChange(e) {
    setForm({ ...form, [e.target.name]: e.target.value });
  }

  function openModal(milestone = null) {
    if (milestone) {
      setEditingMilestone(milestone);
      setForm({
        title: milestone.title,
        due_date: milestone.due_date ? milestone.due_date.split("T")[0] : "",
        amount: milestone.amount || "",
        status: milestone.status || "pending",
      });
    } else {
      setEditingMilestone(null);
      setForm({ title: "", due_date: "", amount: "", status: "pending" });
    }
    setShowModal(true);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!selectedProject) return alert("Please select a project first.");

    try {
      if (editingMilestone) {
        await API.put(`/milestones/${editingMilestone.id}`, form);
        setMessage("Milestone updated successfully!");
      } else {
        await API.post("/milestones", { ...form, project_id: selectedProject });
        setMessage("Milestone created successfully!");
      }
      setShowModal(false);
      setEditingMilestone(null);
      loadMilestones(selectedProject);
    } catch (err) {
      console.error(err);
      setMessage(err.response?.data?.error || err.message);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this milestone?")) return;
    try {
      await API.delete(`/milestones/${id}`);
      loadMilestones(selectedProject);
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    }
  }

  function canVerify() {
    if (!user) return false;
    if (user.role === "admin") return true;
    if (user.role === "manager") {
      const p = projects.find((x) => String(x.id) === String(selectedProject));
      if (p) return Number(p.manager_id) === Number(user.id);
      return true;
    }
    return false;
  }

  function setMilestoneLoading(id, v) {
    setLoadingVerify((s) => ({ ...s, [id]: !!v }));
  }

  async function verifyPayment(milestone) {
    if (!canVerify()) {
      setMessage("You don't have permission to verify payments.");
      return;
    }
    setMilestoneLoading(milestone.id, true);
    const prev = milestones;
    setMilestones((cur) =>
      cur.map((m) =>
        m.id === milestone.id ? { ...m, payment_verified: true } : m
      )
    );

    try {
      const res = await API.post(`/milestones/${milestone.id}/verify-payment`);
      if (res?.data?.milestone) {
        setMilestones((cur) =>
          cur.map((m) =>
            m.id === res.data.milestone.id ? res.data.milestone : m
          )
        );
      } else {
        await loadMilestones(selectedProject);
      }
      setMessage("Milestone payment verified.");
    } catch (err) {
      console.error("[verifyPayment] error:", err);
      setMilestones(prev);
      setMessage(err.response?.data?.error || err.message);
    } finally {
      setMilestoneLoading(milestone.id, false);
    }
  }

  async function unverifyPayment(milestone) {
    if (!canVerify()) {
      setMessage("You don't have permission to unverify payments.");
      return;
    }
    if (!window.confirm("Unverify this milestone's payment?")) return;

    setMilestoneLoading(milestone.id, true);
    const prev = milestones;
    setMilestones((cur) =>
      cur.map((m) =>
        m.id === milestone.id ? { ...m, payment_verified: false } : m
      )
    );

    try {
      const res = await API.post(`/milestones/${milestone.id}/unverify-payment`);
      if (res?.data?.milestone) {
        setMilestones((cur) =>
          cur.map((m) =>
            m.id === res.data.milestone.id ? res.data.milestone : m
          )
        );
      } else {
        await loadMilestones(selectedProject);
      }
      setMessage("Milestone payment unverified.");
    } catch (err) {
      console.error("[unverifyPayment] error:", err);
      setMilestones(prev);
      setMessage(err.response?.data?.error || err.message);
    } finally {
      setMilestoneLoading(milestone.id, false);
    }
  }

  async function onTogglePaid(milestone) {
    if (milestone.payment_verified) {
      await unverifyPayment(milestone);
    } else {
      await verifyPayment(milestone);
    }
  }

  return (
    <div className="milestones-page">
      <div className="header-row">
        <h2>Milestones Management</h2>

        <select
          className="dropdown"
          value={selectedProject}
          onChange={(e) => setSelectedProject(e.target.value)}
        >
          <option value="">-- Choose Project --</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>

        <button className="add-btn" onClick={() => openModal()}>
          <Plus size={18} /> Add Milestone
        </button>
      </div>

      <table className="milestones-table">
        <thead>
          <tr>
            <th>Paid</th>
            <th>Title</th>
            <th>Due Date</th>
            <th>Amount</th>
            <th>Status</th>
            <th>Verified</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {milestones.length > 0 ? (
            milestones.map((m) => {
              const busy = !!loadingVerify[m.id];
              return (
                <tr key={m.id}>
                  <td className="paid-cell">
                    {canVerify() ? (
                      <label
                        className={`paid-toggle ${
                          m.payment_verified ? "on" : "off"
                        }`}
                        style={{ opacity: busy ? 0.6 : 1 }}
                      >
                        <input
                          type="checkbox"
                          checked={!!m.payment_verified}
                          onChange={() => onTogglePaid(m)}
                          disabled={busy}
                        />
                        <span className="paid-switch" />
                      </label>
                    ) : m.payment_verified ? (
                      <CheckCircle className="verified-icon" size={18} />
                    ) : (
                      <XCircle className="not-verified-icon" size={18} />
                    )}
                  </td>
                  <td>{m.title}</td>
                  <td>
                    {m.due_date
                      ? new Date(m.due_date).toLocaleDateString()
                      : "-"}
                  </td>
                  <td>{m.amount != null ? m.amount : "-"}</td>
                  <td>{m.status}</td>
                  <td>
                    {m.payment_verified ? (
                      <span className="verified-badge">Verified</span>
                    ) : (
                      <span className="not-verified">Not verified</span>
                    )}
                  </td>
                  <td>
                    <button
                      className="edit-btn"
                      onClick={() => openModal(m)}
                      disabled={busy}
                    >
                      <Pencil size={16} /> Edit
                    </button>
                    <button
                      className="delete-btn"
                      onClick={() => handleDelete(m.id)}
                      disabled={busy}
                    >
                      <Trash2 size={16} /> Delete
                    </button>
                  </td>
                </tr>
              );
            })
          ) : selectedProject ? (
            <tr>
              <td colSpan="7" className="empty">
                No milestones for this project
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>{editingMilestone ? "Edit Milestone" : "Add New Milestone"}</h3>
            {message && (
              <div
                className={`message ${
                  message.toLowerCase().includes("success")
                    ? "success"
                    : "error"
                }`}
              >
                {message}
              </div>
            )}
            <form onSubmit={handleSubmit} className="milestone-form">
              <input
                type="text"
                name="title"
                placeholder="Milestone Title"
                value={form.title}
                onChange={handleChange}
                required
              />
              <input
                type="date"
                name="due_date"
                value={form.due_date}
                onChange={handleChange}
              />
              <input
                type="number"
                name="amount"
                placeholder="Amount (optional)"
                value={form.amount}
                onChange={handleChange}
              />
              <select name="status" value={form.status} onChange={handleChange}>
                <option value="pending">Pending</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
              </select>

              <div className="modal-actions">
                <button type="submit">
                  {editingMilestone ? "Update" : "Add"} Milestone
                </button>
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
